import {
  DefaultError,
  InfiniteData,
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { Endpoints, Permission, Trait } from '@/api';
import { useFlags } from '@/services/auth';
import { unique } from '@/utils/array';
import { useFetch } from '@/utils/fetch';

import { ArticleWithRelations } from './request';
import { UserRoleScope } from './userRole';
import { getTraitsToActOnBehalfOfNonDigitalizedWorkshop, WorkflowPublic } from './workflow';

const userRoleHasPermission = (
  userRole: UserFromGetSession['roles'][number],
  permission: Permission,
  scope: UserRoleScope
) => {
  // `scope` must have at least one non-nil value to be considered valid
  if (!Object.values(scope).some((value) => value !== null && value !== undefined)) {
    return false;
  }
  const isRoleMatchingScope =
    ('organizationId' in scope ? scope.organizationId === userRole.organizationId : true) &&
    ('entityId' in scope ? scope.entityId === userRole.entityId : true);

  return isRoleMatchingScope && userRole.role.permissions.includes(permission);
};

const userRoleHasPermissionOnAnyScope = (
  userRole: UserFromGetSession['roles'][number],
  permission: Permission,
  organizationId?: string | null
) => {
  if (organizationId != null && userRole.organizationId !== organizationId) {
    return false;
  }

  return userRole.role.permissions.includes(permission);
};

export const hasPermission = (
  user: UserFromGetSession | undefined | null,
  permission: Permission,
  scope: UserRoleScope | (UserRoleScope | null)[]
) => {
  return !!user?.roles.some((userRole) => {
    if (Array.isArray(scope)) {
      return scope.some((s) => (s ? userRoleHasPermission(userRole, permission, s) : false));
    }

    return userRoleHasPermission(userRole, permission, scope);
  });
};

/**
 * Checks if a user has a permission on any of their scopes (org-wide or entity).
 * When `organizationId` is provided, only roles on that organization are considered —
 * mirroring how the API filters credentials scopes by the current organization.
 */
export const hasPermissionOnAnyScope = (
  user: UserFromGetSession | undefined | null,
  permission: Permission,
  organizationId?: string | null
) => {
  return !!user?.roles.some((userRole) =>
    userRoleHasPermissionOnAnyScope(userRole, permission, organizationId)
  );
};

export const hasRoleOnEntity = (user: UserFromGetSession | undefined | null, entityId: string) => {
  return user?.roles.some((userRole) => userRole.entityId === entityId);
};

export const hasRoleOnOrganization = (
  user: UserFromGetSession | undefined | null,
  organizationId: string
) => {
  return user?.roles.some((userRole) => userRole.organizationId === organizationId);
};

export const getScopesForTraits = ({
  traits,
  request,
  article,
}: {
  traits: Trait[];
  request: {
    organizationId: string;
    storeId: string | null;
    afterSalesId: string | null;
  };
  article?: ArticleWithRelations;
}): (UserRoleScope | null)[] => {
  return traits.flatMap((trait) => {
    if (trait === 'aftersales') {
      const afterSalesTraits: UserRoleScope[] = [
        {
          organizationId: request.organizationId,
          entityId: null,
        },
      ];

      if (request.afterSalesId) {
        afterSalesTraits.push({
          entityId: request.afterSalesId,
        });
      }

      return afterSalesTraits;
    }

    if (trait === 'store' && request.storeId) {
      return {
        entityId: request.storeId,
      };
    }

    if (trait === 'workshop' && article?.workshopId) {
      return {
        entityId: article.workshopId,
      };
    }

    return null;
  });
};

export const canActOnBehalfOfNonDigitalizedWorkshop = ({
  user,
  request,
  workflow,
}: {
  user: UserFromGetSession | undefined | null;
  request: {
    organizationId: string;
    storeId: string | null;
    afterSalesId: string | null;
  };
  workflow: WorkflowPublic | undefined | null;
}) => {
  return hasPermission(
    user,
    'act_on_behalf_of_non_digitalized_workshop',
    getScopesForTraits({
      traits: getTraitsToActOnBehalfOfNonDigitalizedWorkshop({ workflow }),
      request,
    })
  );
};

export const getOrganizations = (user: UserFromGetSession | undefined | null) => {
  return unique(
    user?.roles
      .filter((userRole) => !!userRole.organization)
      .map((userRole) => userRole.organization!) || [],
    'id'
  );
};

export const getUserStoresWithPermission = (
  user: UserFromGetSession | undefined | null,
  permission: Permission,
  organizationId: string
) => {
  return (
    user?.roles
      .filter(
        (role) =>
          role.role.permissions.includes(permission) &&
          !!role.entity?.entityOrganizations.some(
            (entityOrganization) =>
              entityOrganization.organizationId === organizationId &&
              entityOrganization.trait === 'store'
          )
      )
      .map((role) => role.entity!) ?? []
  );
};

export const canCreateShipments = (user: UserFromGetSession | undefined | null) => {
  return (
    hasPermissionOnAnyScope(user, 'act_on_behalf_of_non_digitalized_workshop') ||
    hasPermissionOnAnyScope(user, 'create_shipment_from_origin')
  );
};

export const canVerifyReceptionOfShipments = (user: UserFromGetSession | undefined | null) => {
  return hasPermissionOnAnyScope(user, 'verify_shipment_reception');
};

export const isExternalWorkshopUser = (user: UserFromGetSession | undefined | null) => {
  if (!user || user?.roles.some((role) => !!role.organizationId || !role.entityId)) {
    return false;
  }

  const entityOrganizations = unique(
    user.roles.flatMap((role) => role.entity?.entityOrganizations ?? []),
    'id'
  );

  return (
    !!entityOrganizations.length &&
    entityOrganizations.every(
      (entityOrganization) => entityOrganization.trait === 'workshop' && entityOrganization.external
    )
  );
};

export const getUserWorkshops = (user: UserFromGetSession | undefined | null) => {
  const workshops =
    user?.roles
      ?.filter(
        (role) =>
          !!role.entity?.entityOrganizations.some(
            (entityOrganization) => entityOrganization.trait === 'workshop'
          )
      )
      .map((role) => role.entity!) ?? [];

  return workshops;
};

export const getUserStores = (user: UserFromGetSession | undefined | null) => {
  const stores =
    user?.roles
      ?.filter(
        (role) =>
          !!role.entity?.entityOrganizations.some(
            (entityOrganization) => entityOrganization.trait === 'store'
          )
      )
      .map((role) => role.entity!) ?? [];

  return stores;
};

export const isExternalUserForOrganization = (
  user: UserFromGetSession | undefined | null,
  organizationId: string
) => {
  return !user?.roles.find((role) => role.organizationId === organizationId);
};

export type UserFromGetSession = Exclude<
  Endpoints['GET /sessions/current']['response']['user'],
  null
>;
export type UserFromGetMany = Endpoints['GET /users']['response']['users'][number];
export type UserFromGetOne = Endpoints['GET /users/:id']['response'];

export const useUser = (id: string | undefined) => {
  const fetch = useFetch<Endpoints['GET /users/:id']>();

  return useQuery({
    queryKey: ['users', id],
    queryFn: () => fetch(`/users/${id}`),
    enabled: !!id,
  });
};

export const useUsers = (
  params: Endpoints['GET /users']['query'] = {},
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /users']>();

  return useQuery({
    queryKey: ['users', params],
    queryFn: () => fetch('/users', params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
  });
};

export const useInfiniteUsers = (
  params: Omit<Endpoints['GET /users']['query'], 'page' | 'perPage'> = {},
  options?: {
    enabled?: boolean;
    perPage?: number;
  }
) => {
  const fetch = useFetch<Endpoints['GET /users']>();
  const limit = params.limit ?? 20;

  return useInfiniteQuery<
    Endpoints['GET /users']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /users']['response']>,
    QueryKey,
    number
  >({
    queryFn: ({ pageParam }) => fetch('/users', { ...params, offset: pageParam }),
    queryKey: ['users', params, options?.perPage],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.users.length < limit) {
        return undefined;
      } else {
        return lastPage.meta.offset + limit;
      }
    },
    enabled: options?.enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
  });
};

export type UserNotificationSettings =
  Endpoints['GET /users/:id/settings']['response']['notifications'];

export type NotificationCategory = keyof UserNotificationSettings;

export const useUserSettings = (id: string) => {
  const fetch = useFetch<Endpoints['GET /users/:id/settings']>();

  return useQuery({
    queryKey: ['users', id, 'settings'],
    queryFn: () => fetch(`/users/${id}/settings`),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateUserSettings = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /users/:id/settings']>();

  return useMutation({
    mutationFn: ({
      userId,
      ...body
    }: { userId: string } & Endpoints['PATCH /users/:id/settings']['body']) => {
      const settingsQueryKey = ['users', userId, 'settings'];

      const currentSettings =
        queryClient.getQueryData<Endpoints['GET /users/:id/settings']['response']>(
          settingsQueryKey
        );

      const updatedSettings = {
        ...currentSettings,
        ...body,
      };

      queryClient.setQueryData(settingsQueryKey, updatedSettings);

      return fetch(`/users/${userId}/settings`, undefined, { method: 'PATCH', body });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export type MfaMethod = Extract<
  Endpoints['POST /sessions']['response'],
  { mfaToken: string }
>['mfaMethods'][number];

export const useCreateSession = () => {
  const fetch = useFetch<Endpoints['POST /sessions']>();

  return useMutation({
    mutationFn: (params: { email: string; password: string }) =>
      fetch('/sessions', undefined, {
        method: 'POST',
        body: params,
      }),
  });
};

export const useDeleteCurrentSession = () => {
  const fetch = useFetch<Endpoints['DELETE /sessions/current']>();

  return useMutation({
    mutationFn: () =>
      fetch('/sessions/current', undefined, {
        method: 'DELETE',
      }).then(() => {
        localStorage.clear();
        // eslint-disable-next-line react-compiler/react-compiler
        window.location.href = '/login';
      }),
  });
};

export const useCompleteMfa = () => {
  const fetch = useFetch<Endpoints['POST /sessions/complete-mfa']>();

  return useMutation({
    mutationFn: (body: { mfaMethod: MfaMethod['type']; mfaToken: string; code: string }) =>
      fetch('/sessions/complete-mfa', undefined, {
        method: 'POST',
        body,
      }),
  });
};

export const useRequestPasswordReset = () => {
  const fetch = useFetch<Endpoints['POST /auth/reset-password']>();

  return useMutation({
    mutationFn: (email: string) =>
      fetch('/auth/reset-password', undefined, {
        method: 'POST',
        body: { email },
      }),
  });
};

export const useSetNewPassword = () => {
  const fetch = useFetch<Endpoints['POST /auth/reset-password']>();

  return useMutation({
    mutationFn: (params: { email: string; token: string; newPassword: string }) =>
      fetch('/auth/reset-password', undefined, {
        method: 'POST',
        body: params,
      }),
  });
};

export const useUpdateUser = () => {
  const fetch = useFetch<Endpoints['PATCH /users/:id']>();

  return useMutation({
    mutationFn: (params: { id: string; data: { name: string } }) =>
      fetch(`/users/${params.id}`, undefined, {
        method: 'PATCH',
        body: params.data,
      }),
  });
};

export const useProfilePhoto = (userId?: string) => {
  const fetch = useFetch<Endpoints['GET /media/profile/:userId']>();

  return useQuery({
    queryKey: ['media', userId],
    queryFn: () =>
      fetch(`/media/profile/${userId}`).then((media) => {
        if (!media) {
          return null;
        }

        return media.url;
      }),
    // Set a cache to 1 hour as the profile photo is not expected to change often
    staleTime: 55 * 60 * 1000,
    gcTime: 60 * 60 * 1000 + 5, // Stale time + 5 minutes like it's done for the default values
    enabled: !!userId,
  });
};

export const useCheckSsoQuery = (email: string) => {
  const fetch = useFetch<Endpoints['GET /sso/check']>();
  const { flags } = useFlags();

  return useQuery({
    queryKey: ['sso', 'check', email],
    queryFn: () => fetch('/sso/check', { email }),
    enabled: flags?.['enable-sso-authentication'] && !!email,
  });
};

export const useCheckSsoMutation = () => {
  const fetch = useFetch<Endpoints['GET /sso/check']>();

  return useMutation({
    mutationFn: (email: string) => fetch('/sso/check', { email }),
  });
};
